import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Container = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 2rem 1rem;
  position: relative;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  
  @media (max-width: 768px) {
    top: 16px;
    left: 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: #4f46e5;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    color: #4338ca;
  }
`;

const Title = styled.h1`
  color: #4f46e5;
  border-bottom: 2px solid #4f46e5;
  padding-bottom: 12px;
  margin-bottom: 16px;
  font-size: 2rem;
  font-weight: 700;
`;

const UpdateDate = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const Section = styled.section`
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  color: #111827;
  margin-top: 1.75rem;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
`;

const SubSectionTitle = styled.h3`
  margin-top: 1.125rem;
  margin-bottom: 0.375rem;
  font-size: 1rem;
  color: #374151;
  font-weight: 600;
`;

const Paragraph = styled.p`
  margin: 0.5rem 0;
  line-height: 1.75;
  color: #1f2937;
`;

const List = styled.ul`
  padding-left: 1.25rem;
  margin: 0.5rem 0;
`;

const ListItem = styled.li`
  margin: 0.375rem 0;
  line-height: 1.75;
  color: #1f2937;
`;

const Note = styled.div`
  background: #f9fafb;
  border-left: 3px solid #4f46e5;
  padding: 10px 12px;
  margin: 12px 0;
  color: #374151;
`;

const Footer = styled.p`
  margin-top: 2.5rem;
  font-size: 0.875rem;
  color: #6b7280;
`;

 

const PrivacyPage = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <Helmet>
        <title>隐私协议 - 职唛AI</title>
        <meta name="description" content="职唛AI隐私协议 - 了解我们如何收集、使用和保护您的个人信息" />
      </Helmet>
      <Container>
        <BackButtonContainer>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={18} />
            返回
          </BackButton>
        </BackButtonContainer>
        <ContentWrapper>
          <Title>职唛AI隐私协议</Title>
          <UpdateDate><strong>最后更新日期：2026年1月</strong></UpdateDate>

          <Paragraph>
            欢迎使用职唛AI（"本产品"、"我们"）。我们深知个人信息对您的重要性，并会尽全力保护您的隐私安全。本《隐私协议》适用于您使用职唛AI网站、移动端网页及相关功能与服务，包括但不限于：AI 求职助手对话、简历诊断与报告导出、面试准备与模拟面试、语音识别/合成、简历模板下载等功能。
          </Paragraph>

          <Section>
            <SectionTitle>一、我们如何收集与使用个人信息</SectionTitle>
            <Note>
              特别提示：我们坚持“最小化收集”原则，绝不主动收集您的敏感个人身份信息（如身份证号、银行账号、生物识别底库等），亦不会将您的数据用于非法交易。
            </Note>
            
            <SubSectionTitle>1. 账户与身份信息</SubSectionTitle>
            <Paragraph>
              为完成注册/登录及账号管理，我们可能收集：邮箱地址、登录密码（以加密形式保存）、手机号码（如您选择手机注册或绑定时）、验证码、昵称/头像（如您提供或授权）、邀请码及邀请关系信息。这些信息仅用于验证您的身份、完成账号注册/登录和安全管理，不会用于与上述目的无关的其他用途。
            </Paragraph>

            <SubSectionTitle>2. 简历与职业信息</SubSectionTitle>
            <Paragraph>
              当您使用简历诊断、职位匹配、面试准备相关功能时，您可能上传/粘贴简历、职位JD与求职偏好信息。我们使用这些信息来生成诊断报告、优化建议与题目推荐。
            </Paragraph>
            <Paragraph>我们承诺：</Paragraph>
            <List>
              <ListItem>仅将上述信息用于向您提供对应服务和生成报告。</ListItem>
              <ListItem>未经您明确授权，不会将您的简历原文直接公开或分享给任何第三方招聘方。</ListItem>
              <ListItem>对于上传的文件（如 PDF），系统提取必要文本后，原始文件会进行加密存储或定期清理。</ListItem>
            </List>

            <SubSectionTitle>3. 交流内容与使用记录</SubSectionTitle>
            <Paragraph>
              包括与 AI 助手的聊天记录、模拟面试问答内容、评价与反馈、功能点击与使用日志、错误日志等，主要用于：
            </Paragraph>
            <List>
              <ListItem>为您提供、维护与优化服务体验</ListItem>
              <ListItem>改进模型与算法效果（通常以匿名/去标识化方式统计与分析）。请注意：请勿在对话中输入您或他人的敏感隐私信息（如密码、具体家庭住址、金融账户等）。</ListItem>
              <ListItem>保障服务安全与合规</ListItem>
            </List>

            <SubSectionTitle>4. 设备与网络信息</SubSectionTitle>
            <Paragraph>
              为保障产品功能与安全，我们可能收集设备信息、浏览器类型、操作系统版本、IP 地址、日志与诊断信息、网络延迟与错误上报等。
            </Paragraph>

            <SubSectionTitle>5. 音视频相关数据（可选开启）</SubSectionTitle>
            <Paragraph>
              当您使用语音输入（ASR）、语音播报（TTS）、模拟面试、表情/神态分析等能力时，我们可能在获得您的授权后访问麦克风/摄像头，仅在会话期间采集相应音视频流或提取必要特征，用于识别、反馈与生成报告。我们不会保存个人的原始音视频内容。
            </Paragraph>

            <Note>
              提示：您可在浏览器层面管理麦克风/摄像头授权；关闭授权后相关功能将不可用。
            </Note>
          </Section>

          <Section>
            <SectionTitle>二、Cookie 与本地存储技术</SectionTitle>
            <Paragraph>
              我们使用 Cookie、LocalStorage 或同类技术以记住登录状态、保存必要的偏好设置、提升加载速度与体验，并用于基础的匿名统计。您可通过浏览器设置清理或禁用 Cookie，但可能影响部分功能的连续性。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>三、我们如何共享、转移与公开披露个人信息</SectionTitle>
            <List>
              <ListItem>
                <strong>共享</strong>：仅在以下情形下共享必要信息：获得您的明确同意；基于法律法规、监管要求；与受严格数据保护约束的服务供应商（如短信服务、AI 模型推理/语音处理等）履行委托处理；为保护您、我们或公众的权益所必需。
              </ListItem>
              <ListItem>
                <strong>转移</strong>：发生合并、分立、收购或资产转让等情形时，如涉及个人信息转移，我们将要求受让方继续受本协议约束，否则将重新获取您的授权。
              </ListItem>
              <ListItem>
                <strong>公开披露</strong>：原则上不公开披露您的个人信息；如确有必要（如公示中奖名单），将采用去标识化处理并征得您的同意或依法披露。
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>四、第三方服务</SectionTitle>
            <Paragraph>
              为实现短信验证、模型推理、语音处理、文件预览与导出、行为统计等，我们可能集成第三方 SDK 或 API。这些第三方会依据其政策处理数据，我们将尽合理努力评估其合规性并限制其数据访问范围。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>五、个人信息的存储与保护</SectionTitle>
            <List>
              <ListItem>
                <strong>存储地点</strong>：原则上在中华人民共和国境内存储。
              </ListItem>
              <ListItem>
                <strong>存储期限</strong>：为实现服务目的所必需的最短期限，超过期限将删除或匿名化处理；您也可依据本协议第八条行使删除权。
              </ListItem>
              <ListItem>
                <strong>安全措施</strong>：采用加密传输、访问控制、最小化授权、日志审计、漏洞修复与员工安全培训等方式保护信息安全。
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>六、免责声明与风险提示</SectionTitle>
            <List>
              <ListItem>
                AI生成内容的准确性：本产品基于人工智能模型技术提供服务，受限于技术发展水平，我们无法保证AI生成内容（包括但不限于简历建议、面试回答、模拟评分等）的完全准确性、完整性或适用性。生成内容仅供参考，不应作为您做出重大决策（如签署合同、法律诉讼、医疗诊断等）的唯一依据。
              </ListItem>
              <ListItem>
                用户合规使用：您承诺不使用本产品进行任何作弊、欺诈、违法或违规行为。因您违反法律法规或本协议使用服务而产生的任何法律后果，由您自行承担，与我们无关。
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>七、未成年人保护</SectionTitle>
            <Paragraph>
              我们不主动面向未满 18 周岁的未成年人提供服务或收集其个人信息。如您为监护人并认为我们误收集了未成年人的信息，请与我们联系以便及时删除或进行其他必要处置。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>八、您的权利</SectionTitle>
            <Paragraph>在法律法规规定范围内，您有权：</Paragraph>
            <List>
              <ListItem>查询、访问、复制、校正您的个人信息</ListItem>
              <ListItem>撤回授权或不同意某些处理（将可能影响相关功能）</ListItem>
              <ListItem>删除个人信息或注销账户（在不影响依法留存的情况下）</ListItem>
              <ListItem>获取数据副本与可携带性（符合条件时）</ListItem>
              <ListItem>获取我们关于数据处理规则的说明</ListItem>
            </List>

            <SubSectionTitle>行使方式</SubSectionTitle>
            <Paragraph>
              您可通过产品内「意见反馈」或以下联系方式提交申请，我们将在验证您身份后按法规时限予以处理。为保障安全，我们可能会要求您提供进一步信息以核验身份。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>九、本协议的更新</SectionTitle>
            <Paragraph>
              我们可能适时更新本协议。当发生重大变更（如处理目的、类型、共享对象发生变化）时，我们将通过站内公告、弹窗或其他合理方式提示，并在获得必要授权后再行处理。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>十、联系我们</SectionTitle>
            <Paragraph>运营主体：杭州元烁智创科技有限公司</Paragraph>
            <Paragraph>地址：浙江省杭州市萧山区德必港立WE产业园A座315室</Paragraph>
            <Paragraph>联系渠道：应用内反馈或客服邮箱 contact@zhispark.com</Paragraph>
          </Section>

          <Footer>
            感谢您信任职唛AI。我们将持续以业界通行的安全实践保护您的隐私与数据安全。
          </Footer>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default PrivacyPage;
